import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemButton,
  Collapse,
  Chip,
  AppBar,
  Toolbar,
  Button
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  GroupWork as GroupWorkIcon,
  LibraryBooks as LibraryBooksIcon,
  Quiz as QuizIcon,
  Assignment as AssignmentIcon,
  BarChart as BarChartIcon,
  EventNote as EventNoteIcon,
  EmojiEvents as EmojiEventsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ContactSupport,
  Person as PersonIcon,
  PersonAdd,
  VpnKey as VpnKeyIcon,
  ExitToApp,
  ExpandLess,
  ExpandMore,
  Help as HelpIcon,
  School as SchoolIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  EventAvailable as EventAvailableIcon,
  Book as BookIcon,
  CalendarToday as CalendarIcon,
  EmojiEvents as AwardsIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import {
  handleProfile,
  handleChangePassword,
  handleLogout
} from '../common/GeneralnNagivationHandler';
import SupportDialog from '../SupportDialog';
import InviteDialog from '../InviteDialog';
import NudgeDialog from '../NudgeDialog';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const RoleBasedLayout = ({ 
  children,
  open, 
  onClose, 
  collapsed = false, 
  onToggleCollapse
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);

  // Role-based navigation menu items
  const getMenuItems = () => {
    if (!user) return [];

    const isAdmin = user.isAdmin || user.is_admin;
    const isTeacher = user.isTeacher || user.is_teacher;
    const isStudent = user.isStudent || user.is_student;
    const isParent = user.isParent || user.is_parent;

    if (isAdmin) {
      return [
        {
          group: 'Main',
          items: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', ariaLabel: 'Navigate to Dashboard' }
          ]
        }
      ];
    } else if (isTeacher) {
      return [
        {
          group: 'Main',
          items: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', ariaLabel: 'Navigate to Dashboard' }
          ]
        },
        {
          group: 'Students',
          items: [
            { text: 'Manage Students', icon: <GroupIcon />, path: '/teacher/students', ariaLabel: 'Manage students' },
            { text: 'Manage Classes', icon: <GroupWorkIcon />, path: '/teacher/batches', ariaLabel: 'Manage classes' },
          ]
        },
        {
          group: 'Test Management',
          items: [
            { text: 'Exam Prep Plan', icon: <EventAvailableIcon />, path: '/teacher/exam-prep', ariaLabel: 'Exam preparation plans' },
            { text: 'Test Library', icon: <QuizIcon />, path: '/teacher/tests', ariaLabel: 'Test library' },
            { text: 'Assign Tests', icon: <AssignmentIcon />, path: '/teacher/assignments', ariaLabel: 'Assign tests to students' },
          ]
        },
        {
          group: 'Content',
          items: [
            { text: 'Question Bank', icon: <LibraryBooksIcon />, path: '/teacher/questionbank', ariaLabel: 'Access Question Bank' },
          ]
        },
        {
          group: 'Insights',
          items: [
            { text: 'Analytics', icon: <BarChartIcon />, path: '/teacher/analytics', ariaLabel: 'View Analytics' },
          ]
        },
        {
          group: 'Settings',
          items: [
            { text: 'Calendar', icon: <EventNoteIcon />, path: '/teacher/calendar', ariaLabel: 'Access Calendar' },
            { text: 'Rewards & Recognition', icon: <EmojiEventsIcon />, path: '/teacher/awards', ariaLabel: 'Rewards and recognition' },
          ]
        }
      ];
    } else if (isStudent) {
      return [
        {
          group: 'Main',
          items: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', ariaLabel: 'Navigate to Dashboard' }
          ]
        },
        {
          group: 'Learning',
          items: [
            { text: 'My Tests', icon: <QuizIcon />, path: '/student/tests', ariaLabel: 'View My Tests' },
            { text: 'My Assignments', icon: <AssignmentIcon />, path: '/student/assignments', ariaLabel: 'View My Assignments' },
            { text: 'My Results', icon: <AssessmentIcon />, path: '/student/results', ariaLabel: 'View My Results' },
            { text: 'Revision Notes', icon: <BookIcon />, path: '/student/revision-notes', ariaLabel: 'Access Revision Notes' }
          ]
        },
        {
          group: 'Progress',
          items: [
            { text: 'My Progress', icon: <BarChartIcon />, path: '/student/progress', ariaLabel: 'View My Progress' },
            { text: 'My Awards', icon: <AwardsIcon />, path: '/student/awards', ariaLabel: 'View My Awards' },
            { text: 'My Calendar', icon: <CalendarIcon />, path: '/student/calendar', ariaLabel: 'View My Calendar' }
          ]
        }
      ];
    } else if (isParent) {
      return [
        {
          group: 'Main',
          items: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', ariaLabel: 'Navigate to Dashboard' }
          ]
        },
        {
          group: 'Family',
          items: [
            { text: 'My Children', icon: <GroupIcon />, path: '/parent/children', ariaLabel: 'Manage My Children' },
            { text: 'Assignments', icon: <AssignmentIcon />, path: '/parent/assignments', ariaLabel: 'View Assignments' },
            { text: 'Progress Reports', icon: <AssessmentIcon />, path: '/parent/progress', ariaLabel: 'View Progress Reports' },
            { text: 'Exam prep', icon: <EventAvailableIcon />, path: '/parent/exam-prep', ariaLabel: 'Exam preparation plans' }
          ]
        }
      ];
    }

    return [];
  };

  const menuItems = getMenuItems();

  const handleProfileClick = () => {
    handleProfile(navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleChangePasswordClick = () => {
    handleChangePassword(navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleLogout(logout, navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleSupportOpen = () => {
    setSupportOpen(true);
  };

  const handleInviteOpen = () => {
    setInviteOpen(true);
  };

  const isActive = (path) => {
    if (path === '/teacher/batches') {
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    }
    return location.pathname === path;
  };

  const renderMenuItem = (item) => {
    const active = isActive(item.path);
    
    const menuItem = (
      <ListItemButton
        key={item.text}
        onClick={() => navigate(item.path)}
        selected={active}
        sx={{
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          minHeight: 48,
          position: 'relative',
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.16)',
            color: '#FFFFFF',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.24)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              backgroundColor: '#1976d2',
              borderRadius: '0 2px 2px 0'
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: '#FFFFFF',
          }
        }}
        aria-label={item.ariaLabel}
      >
        <ListItemIcon 
          sx={{ 
            minWidth: collapsed ? 40 : 48,
            color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText 
            primary={item.text}
            primaryTypographyProps={{
              fontWeight: active ? 600 : 400,
              color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
            }}
          />
        )}
      </ListItemButton>
    );

    // Add tooltip when collapsed
    if (collapsed) {
      return (
        <Tooltip 
          key={item.text}
          title={item.text} 
          placement="right"
          arrow
        >
          {menuItem}
        </Tooltip>
      );
    }

    return menuItem;
  };

  const renderMenuGroup = (group) => {
    if (collapsed) {
      return group.items.map(renderMenuItem);
    }

    return (
      <Box key={group.group}>
        <Typography
          variant="caption"
          sx={{
            px: 3,
            py: 1,
            color: 'rgba(255, 255, 255, 0.5)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.75rem'
          }}
        >
          {group.group}
        </Typography>
        {group.items.map(renderMenuItem)}
      </Box>
    );
  };

  const getPageTitle = () => {
    const pathMap = {
      '/dashboard': 'Dashboard',
      '/profile': 'My Profile',
      '/settings/change-password': 'Change Password',
      '/teacher/students': 'Manage Students',
      '/teacher/batches': 'Manage Classes',
      '/teacher/exam-prep': 'Exam Prep Plan',
      '/teacher/questionbank': 'Question Bank',
      '/teacher/tests': 'Test Library',
      '/teacher/assignments': 'Assign Tests',
      '/teacher/analytics': 'Analytics',
      '/teacher/calendar': 'Calendar',
      '/teacher/awards': 'Rewards & Recognition',
      '/admin/users': 'User Management',
      '/admin/settings': 'System Settings',
      '/admin/analytics': 'Analytics',
      '/admin/content': 'Content Management',
      '/student/tests': 'My Tests',
      '/student/assignments': 'My Assignments',
      '/student/results': 'My Results',
      '/student/revision-notes': 'Revision Notes',
      '/student/progress': 'My Progress',
      '/student/awards': 'My Awards',
      '/student/calendar': 'My Calendar',
      '/parent/children': 'My Children',
      '/parent/assignments': 'Assignments',
      '/parent/progress': 'Progress Reports',
      '/parent/exam-prep': 'Exam prep'
    };

    return pathMap[location.pathname] || 'Dashboard';
  };

  const drawerContent = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        px: 2,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.12)'
      }}>
        {!collapsed && (
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
            ThinkStyne
          </Typography>
        )}
        <IconButton
          onClick={onToggleCollapse}
          sx={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': { 
              color: '#FFFFFF',
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {menuItems.map(renderMenuGroup)}
      </Box>

             {/* Footer */}
       {!collapsed && (
         <Box sx={{ 
           p: 2, 
           borderTop: '1px solid', 
           borderColor: 'rgba(255, 255, 255, 0.12)',
           mt: 'auto'
         }}>
           {/* User Profile Section - Mobile Only */}
           {isMobile && (
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
               <Avatar 
                 sx={{ 
                   width: 40, 
                   height: 40, 
                   bgcolor: 'primary.main',
                   fontSize: '1rem'
                 }}
                 src={user?.profile_picture}
               >
                 {user?.first_name?.charAt(0) || 'U'}
               </Avatar>
               <Box sx={{ flex: 1 }}>
                 <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                   {user?.first_name} {user?.last_name}
                 </Typography>
                 <Typography variant="caption" color="text.secondary">
                   {user?.email}
                 </Typography>
               </Box>
             </Box>
           )}
           
           {/* User Menu Items - Mobile Only */}
           {isMobile && (
             <List sx={{ py: 0 }}>
               <ListItemButton
                 onClick={handleProfileClick}
                 sx={{
                   borderRadius: 1,
                   mb: 0.5,
                   minHeight: 48
                 }}
               >
                 <ListItemIcon>
                   <PersonIcon fontSize="small" />
                 </ListItemIcon>
                 <ListItemText>Profile</ListItemText>
               </ListItemButton>
               
               <ListItemButton
                 onClick={handleChangePasswordClick}
                 sx={{
                   borderRadius: 1,
                   mb: 0.5,
                   minHeight: 48
                 }}
               >
                 <ListItemIcon>
                   <VpnKeyIcon fontSize="small" />
                 </ListItemIcon>
                 <ListItemText>Change Password</ListItemText>
               </ListItemButton>
               
               <Divider sx={{ my: 1 }} />
               
               <ListItemButton
                 onClick={handleLogoutClick}
                 sx={{
                   borderRadius: 1,
                   minHeight: 48,
                   color: 'error.main'
                 }}
               >
                 <ListItemIcon>
                   <ExitToApp fontSize="small" color="error" />
                 </ListItemIcon>
                 <ListItemText>Logout</ListItemText>
               </ListItemButton>
             </List>
           )}
         </Box>
       )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isMobile ? open : true}
        onClose={onClose}
        sx={{
          width: collapsed ? collapsedDrawerWidth : drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: collapsed ? collapsedDrawerWidth : drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
            backgroundColor: '#1a237e',
            color: '#FFFFFF'
          }
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Top Bar */}
        <AppBar 
          position="static" 
          elevation={0}
          sx={{ 
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isMobile && (
                <IconButton
                  edge="start"
                  onClick={onClose}
                  sx={{ mr: 2 }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {getPageTitle()}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Invite Button - Mobile version */}
              {!loading && user && (user.isTeacher || user.is_teacher) && (
                <Tooltip title="Invite others and get rewarded">
                  <IconButton
                    onClick={handleInviteOpen}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.50',
                      },
                      // Mobile: show icon only, no border
                      display: { xs: 'flex', sm: 'none' }
                    }}
                  >
                    <PersonAdd />
                  </IconButton>
                </Tooltip>
              )}

              {/* Invite Button - Desktop version */}
              {!loading && user && (user.isTeacher || user.is_teacher) && (
                <Tooltip title="Invite others and get rewarded">
                  <IconButton
                    onClick={handleInviteOpen}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.50',
                      },
                      display: { xs: 'none', sm: 'flex' }
                    }}
                  >
                    <PersonAdd />
                  </IconButton>
                </Tooltip>
              )}

              {/* Nudge Button - Mobile version */}
              {!loading && user && (user.isTeacher || user.is_teacher || user.isParent || user.is_parent) && (
                <Tooltip title="Encourage learners">
                  <IconButton
                    onClick={() => setNudgeOpen(true)}
                    sx={{
                      color: 'secondary.main',
                      '&:hover': {
                        backgroundColor: 'secondary.50',
                      },
                      // Mobile: show icon only, no border
                      display: { xs: 'flex', sm: 'none' }
                    }}
                  >
                    <MessageIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* Nudge Button - Desktop version */}
              {!loading && user && (user.isTeacher || user.is_teacher || user.isParent || user.is_parent) && (
                <Tooltip title="Encourage learners">
                  <IconButton
                    onClick={() => setNudgeOpen(true)}
                    sx={{
                      color: 'secondary.main',
                      '&:hover': {
                        backgroundColor: 'secondary.50',
                      },
                      display: { xs: 'none', sm: 'flex' }
                    }}
                  >
                    <MessageIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {/* Need Help Button - Mobile version */}
              <Tooltip title="Reach out for help">
                <IconButton
                  onClick={handleSupportOpen}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.50',
                    },
                    // Mobile: show icon only, no border
                    display: { xs: 'flex', sm: 'none' }
                  }}
                >
                  <ContactSupport />
                </IconButton>
              </Tooltip>

              {/* Need Help Button - Desktop version */}
              <Tooltip title="Reach out for help">
                <IconButton
                  onClick={handleSupportOpen}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.50',
                    },
                    display: { xs: 'none', sm: 'flex' }
                  }}
                >
                  <ContactSupport />
                </IconButton>
              </Tooltip>

              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ 
                  width: 40, 
                  height: 40,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }
          }}
        >
          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="My Profile" />
          </MenuItem>
          
          <MenuItem onClick={handleChangePasswordClick}>
            <ListItemIcon>
              <VpnKeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Change Password" />
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={handleLogoutClick}>
            <ListItemIcon>
              <ExitToApp fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
          
          <Divider />
          
          <MenuItem disabled>
            <ListItemText 
              primary={user?.email || 'No email'} 
              primaryTypographyProps={{ 
                variant: 'caption', 
                color: 'text.secondary',
                sx: { fontStyle: 'italic' } 
              }} 
            />
          </MenuItem>
        </Menu>

        {/* Page Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          bgcolor: 'background.default'
        }}>
          {children}
        </Box>
      </Box>

      {/* Support Dialog */}
      <SupportDialog 
        open={supportOpen} 
        onClose={() => setSupportOpen(false)} 
        presetEmail={user?.email || ''} 
      />

      {/* Invite Dialog */}
      <InviteDialog 
        open={inviteOpen} 
        onClose={() => setInviteOpen(false)} 
      />

      {/* Nudge Dialog */}
      <NudgeDialog 
        open={nudgeOpen} 
        onClose={() => setNudgeOpen(false)} 
      />
    </Box>
  );
};

export default RoleBasedLayout; 
